import type { GridGuideModalRect } from './gridGuideModalLayout';

export interface GridGuideVisibleBounds {
  top: number;
  bottom: number;
}

export interface GridGuideScrollDeltaOptions {
  region: GridGuideVisibleBounds;
  viewport: GridGuideVisibleBounds;
  margin?: number;
}

const SCROLLABLE_OVERFLOW = new Set(['auto', 'overlay', 'scroll']);

interface GridGuideViewportContext {
  bottomInset: number;
  scrollableAncestor?: HTMLElement;
}

export const getGridGuideScrollDelta = ({
  region,
  viewport,
  margin = 8,
}: GridGuideScrollDeltaOptions): number => {
  if (region.bottom + margin > viewport.bottom) {
    return region.bottom + margin - viewport.bottom;
  }

  if (region.top - margin < viewport.top) {
    return region.top - margin - viewport.top;
  }

  return 0;
};

const getViewportContext = (element: HTMLElement): GridGuideViewportContext => {
  let ancestor = element.parentElement;
  let bottomInset = 0;
  let scrollableAncestor: HTMLElement | undefined;

  while (ancestor) {
    const style = window.getComputedStyle(ancestor);
    const scrollPaddingBottom = Number.parseFloat(
      style.getPropertyValue('scroll-padding-bottom') ||
        style.scrollPaddingBottom
    );

    if (
      !scrollableAncestor &&
      SCROLLABLE_OVERFLOW.has(style.overflowY) &&
      ancestor.scrollHeight > ancestor.clientHeight
    ) {
      scrollableAncestor = ancestor;
    }

    if (Number.isFinite(scrollPaddingBottom)) {
      bottomInset = Math.max(bottomInset, scrollPaddingBottom);
    }

    ancestor = ancestor.parentElement;
  }

  return { bottomInset, scrollableAncestor };
};

const getWindowViewport = (): GridGuideVisibleBounds => {
  const visualViewport = window.visualViewport;
  const top = visualViewport?.offsetTop ?? 0;

  return {
    top,
    bottom: top + (visualViewport?.height ?? window.innerHeight),
  };
};

const getCanvasRegionBounds = (
  canvas: HTMLCanvasElement,
  region: GridGuideModalRect
): GridGuideVisibleBounds => {
  const canvasRect = canvas.getBoundingClientRect();
  const logicalCanvasHeight =
    canvas.clientHeight ||
    Number.parseFloat(canvas.style.height) ||
    canvasRect.height;
  const scaleY =
    logicalCanvasHeight > 0 ? canvasRect.height / logicalCanvasHeight : 1;

  return {
    top: canvasRect.top + region.y * scaleY,
    bottom: canvasRect.top + (region.y + region.h) * scaleY,
  };
};

export const revealGridGuideRegion = (
  canvas: HTMLCanvasElement,
  region: GridGuideModalRect
): number => {
  const { bottomInset, scrollableAncestor } = getViewportContext(canvas);
  const windowViewport = getWindowViewport();
  const visibleWindowBottom = windowViewport.bottom - bottomInset;
  const viewport = scrollableAncestor
    ? (() => {
        const ancestorRect = scrollableAncestor.getBoundingClientRect();

        return {
          top: Math.max(ancestorRect.top, windowViewport.top),
          bottom: Math.min(ancestorRect.bottom, visibleWindowBottom),
        };
      })()
    : {
        ...windowViewport,
        bottom: visibleWindowBottom,
      };
  const delta = getGridGuideScrollDelta({
    region: getCanvasRegionBounds(canvas, region),
    viewport,
  });

  if (delta === 0) {
    return 0;
  }

  if (scrollableAncestor) {
    scrollableAncestor.scrollTop += delta;
  } else {
    window.scrollBy(0, delta);
  }

  return delta;
};
